'use client';
import { Cancel, CheckCircle } from '@mui/icons-material';
import { Box, Checkbox, FormControl, FormControlLabel, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import Explanation from '@/components/exercise/explanation';
import RichContent from '@/components/shared/rich-content';
import { useQuestionMarking } from '@/hooks';
import { useCourses } from '@/providers';
import { useViewStore } from '@/stores';
import type { Question, QuestionType } from '@/types';
import { isExercise } from '@/types';
import { getCorrectLabels, isAnswerCorrect } from '@/utils/server/course';

function CheckboxOption({
  index,
  value,
  label,
  isChecked,
  onChange,
  result,
  preview,
  type,
  solution,
}: {
  index: number;
  value: string;
  label: string;
  isChecked: boolean;
  onChange: (index: string, value: string) => void;
  result?: boolean;
  preview?: boolean;
  type: QuestionType;
  solution?: string[];
}) {
  const getColor = () => {
    if (!result) return undefined;
    const isCorrect = isAnswerCorrect(type, index, solution || []);
    const isTrueValue = value === 'true';
    return isTrueValue === isCorrect ? '#16C36C' : '#FC5055';
  };

  const getFeedback = () => {
    if (!result) return null;
    const isCorrect = isAnswerCorrect(type, index, solution || []);
    const isTrueValue = value === 'true';
    const isCorrectlyAnswered = isTrueValue === isCorrect;
    return isCorrectlyAnswered ? (
      <CheckCircle sx={{ color: '#16C36C' }} fontSize="small" />
    ) : (
      <Cancel sx={{ color: '#FC5055' }} fontSize="small" />
    );
  };

  return (
    <FormControl disabled={result || preview}>
      <FormControlLabel
        control={
          <Checkbox checked={isChecked} onChange={() => onChange(index.toString(), value)} />
        }
        label={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: getColor(),
            }}>
            <Typography variant="body2">{label}</Typography>
            {getFeedback()}
          </Box>
        }
      />
    </FormControl>
  );
}

export default function AnswersCheckboxes({
  question,
  result,
  preview,
}: {
  question: Question;
  result?: boolean;
  preview?: boolean;
}) {
  const { selectedCourse, selectedChapter } = useCourses();
  const { showExplanations } = useViewStore();
  const { id, answers, marking, type, solution } = question;
  const [showIndex, setShowIndex] = useState(0);
  const [initialized, setInitialized] = useState(false);
  const answersLength = answers?.length || 0;
  const [correctAnswerLabel, incorrectAnswerLabel] = getCorrectLabels();

  const { updateMarking } = useQuestionMarking(selectedCourse?.id || '');

  useEffect(() => {
    if (!initialized) {
      const markers = marking;
      if (markers.length) {
        let newShowIndex = 0;
        markers.forEach((marker) => {
          const index = +marker.split(':')[0];
          if (index > showIndex) newShowIndex = index;
        });
        setShowIndex(newShowIndex + 1);
      }
      setInitialized(true);
    }
  }, [marking, showIndex, initialized]);

  const handleChange = (markingIndex: string, newValue: string): void => {
    if (!selectedChapter || !isExercise(selectedChapter)) return;
    updateMarking({
      question,
      update: `${markingIndex}:${newValue}`,
      exercise: selectedChapter,
    });
    if (showIndex <= +markingIndex && showIndex + 1 < answersLength) {
      setShowIndex(showIndex + 1);
    }
  };

  const isChecked = (index: string, value: string): boolean => {
    return marking.indexOf(`${index}:${value}`) > -1;
  };

  const explanationIsActive = showExplanations.indexOf(id) > -1;

  return (
    <Box>
      {answers?.map(({ id, answerNode, explanationNode, index }) => (
        <Box key={id} sx={{ mb: 3 }}>
          <RichContent content={answerNode} noParagraphMargin />
          <Box sx={{ display: 'flex', gap: 3 }}>
            <CheckboxOption
              index={index}
              value="false"
              label={incorrectAnswerLabel}
              isChecked={isChecked(index.toString(), 'false')}
              onChange={handleChange}
              result={result}
              preview={preview}
              type={type}
              solution={solution}
            />
            <CheckboxOption
              index={index}
              value="true"
              label={correctAnswerLabel}
              isChecked={isChecked(index.toString(), 'true')}
              onChange={handleChange}
              result={result}
              preview={preview}
              type={type}
              solution={solution}
            />
          </Box>
          {explanationIsActive && explanationNode && (
            <Box sx={{ mt: 1 }}>
              <Explanation
                explanationNode={explanationNode}
                isCorrect={isAnswerCorrect(type, index, solution || [])}
              />
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
}
